import React from "react";
import { useNavigate } from "react-router-dom";
import AnimatedHeadline from "../AnimatedHeadline";
import RevealSection from "../RevealSection";
import { useInView } from "../../hooks/useInView";

const SIGNAL_ROWS = [
  { name: "Role fit", score: 89, tone: "positive" },
  { name: "Job freshness", score: 76, tone: "positive" },
  { name: "Company health", score: 91, tone: "positive" },
  { name: "Callback odds", score: 68, tone: "mixed" },
];

const CHIPS = ["Java 17", "Spring Boot", "Kafka", "Kubernetes"];

export const Hero = ({ onOpenAuth }) => {
  const navigate = useNavigate();

  const scrollToHowItWorks = () => {
    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      style={{
        background: "#1a1a18",
        padding: "80px 24px 100px",
        minHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        className="hero-grid"
        style={{
          maxWidth: 1100,
          width: "100%",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 48,
          alignItems: "center",
        }}
      >
        {/* Left column — copy */}
        <div style={{ maxWidth: 520 }}>
          <RevealSection>
            <p
              className="font-body"
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#1D9E75",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                marginBottom: 20,
              }}
            >
              BEFORE YOU HIT APPLY
            </p>
          </RevealSection>

          <AnimatedHeadline
            text="Do you actually know what you're applying to?"
            tag="h1"
            className="font-heading"
            style={{
              fontSize: "clamp(44px, 6vw, 72px)",
              fontWeight: 700,
              letterSpacing: "-2px",
              lineHeight: 1.05,
              color: "white",
              marginBottom: 16,
            }}
          />

          <RevealSection delay={3}>
            <p
              className="font-body"
              style={{
                fontSize: 18,
                color: "rgba(255,255,255,0.6)",
                lineHeight: 1.6,
                marginBottom: 40,
              }}
            >
              GuMi tells you if the job is real, if the company is stable, and if your resume
              actually fits, all usually in under 30 seconds.
            </p>
          </RevealSection>

          <RevealSection delay={4}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 320 }}>
              <button
                type="button"
                data-testid="hero-primary-cta"
                onClick={() => (onOpenAuth ? onOpenAuth("register") : navigate("/register"))}
                className="cta-pulse font-body"
                style={{
                  background: "#1D9E75",
                  color: "white",
                  padding: "16px 28px",
                  borderRadius: 12,
                  fontSize: 15,
                  fontWeight: 600,
                  width: "100%",
                  maxWidth: 320,
                  minHeight: 44,
                }}
              >
                Analyze my first job, it's free
              </button>
              <button
                type="button"
                data-testid="hero-secondary-cta"
                onClick={scrollToHowItWorks}
                className="font-body transition-colors"
                style={{
                  background: "transparent",
                  color: "rgba(255,255,255,0.6)",
                  padding: "12px 0",
                  fontSize: 13,
                  textAlign: "left",
                  minHeight: 44,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "white"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.6)"; }}
              >
                See how it works ↓
              </button>
            </div>

          </RevealSection>

          <RevealSection delay={5}>
            <p
              className="font-body"
              style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 20 }}
            >
              ✓ Real-time job signals&nbsp;&nbsp;·&nbsp;&nbsp;✓ AI-powered fit scoring&nbsp;&nbsp;·&nbsp;&nbsp;✓ Built in India
            </p>
          </RevealSection>
        </div>

        {/* Right column — mock product preview */}
        <ProductPreviewCard />
      </div>
    </section>
  );
};

const ProductPreviewCard = () => {
  const { ref, inView } = useInView(0.2);

  return (
    <div
      ref={ref}
      className={`card-reveal ${inView ? "visible" : ""}`}
      style={{ maxWidth: 380, marginLeft: "auto", width: "100%" }}
      data-testid="hero-product-preview"
    >
      <div
        className="product-card-float"
        style={{
          background: "white",
          borderRadius: 16,
          padding: 20,
          boxShadow: "0 32px 80px rgba(0,0,0,0.5)",
        }}
      >
        <p className="font-body" style={{ fontSize: 11, color: "#5f5e5a" }}>
          Sample · Northwind Cloud · Staff Engineer · Remote
        </p>

        <p
          className="font-heading"
          style={{ fontSize: 22, fontWeight: 700, color: "#1D9E75", marginTop: 6 }}
        >
          Worth applying
        </p>

        <div
          style={{ height: 6, borderRadius: 3, background: "#e1f5ee", margin: "8px 0", overflow: "hidden" }}
          role="progressbar"
          aria-valuenow={82}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Composite score"
        >
          <div style={{ height: "100%", width: "82%", background: "#1D9E75", borderRadius: 3 }} />
        </div>
        <p className="font-body" style={{ fontSize: 11, color: "#8a8880", textAlign: "right" }}>
          82 / 100
        </p>

        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          {SIGNAL_ROWS.map((row) => (
            <div key={row.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span className="font-body" style={{ fontSize: 12.5, color: "#1a1a18" }}>{row.name}</span>
              <span
                className="font-body"
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: row.tone === "positive" ? "#E1F5EE" : "#FEF3DC",
                  color: row.tone === "positive" ? "#085041" : "#633806",
                }}
              >
                {row.score} {row.tone === "positive" ? "✅" : "⚠️"}
              </span>
            </div>
          ))}
        </div>

        <p className="font-body" style={{ fontSize: 10, color: "#5f5e5a", marginTop: 16, marginBottom: 6 }}>
          Your strong matches
        </p>
        <div role="list" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {CHIPS.map((chip) => (
            <span
              role="listitem"
              key={chip}
              className="font-body"
              style={{
                fontSize: 10,
                fontWeight: 600,
                background: "#E1F5EE",
                color: "#085041",
                padding: "3px 8px",
                borderRadius: 999,
              }}
            >
              {chip}
            </span>
          ))}
        </div>

        <p className="font-body" style={{ fontSize: 11, color: "#1D9E75", fontWeight: 500, marginTop: 14 }}>
          3 skills to highlight before applying →
        </p>
        <p className="font-body" style={{ fontSize: 10, color: "#8a8880", marginTop: 10 }}>
          Illustrative sample with a fictional company. Not a real analysis.
        </p>
      </div>
    </div>
  );
};

export default Hero;
